import React, { useMemo, useState, useEffect, useRef } from 'react';
import { usePlanStore } from './store/usePlanStore';
import { useAuth } from './store/useAuthStore';
import { useCustomExercisesStore } from './store/useCustomExercisesStore';
import { useSurveyStore } from './store/useSurveyStore';
import Layout from './components/Layout';
import PlanConfig from './components/PlanConfig';
import WeekView from './components/WeekView';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import CoachPanel from './components/CoachPanel';
import LegendPage from './components/LegendPage';
import LoginScreen from './components/LoginScreen';
import LandingPage from './components/LandingPage';
import PlanDashboard from './components/PlanDashboard';
import ExerciseDatabase from './components/ExerciseDatabase';
import SurveyBuilder from './components/SurveyBuilder';
import SurveyFillPage from './components/SurveyFillPage';
import CalculatorsPage from './components/CalculatorsPage';
import TermsPage from './components/TermsPage';
import PrivacyPage from './components/PrivacyPage';
import PlanComparison from './components/PlanComparison';
import CookieConsent from './components/CookieConsent';
import { calculateVolumeMetrics } from './logic/volumeEngine';
import { calculateFatigueMetrics } from './logic/fatigueSystem';
import { generateCoachSuggestions } from './logic/coachEngine';
import { Loader2, Dumbbell } from 'lucide-react';

const API_BASE_URL = import.meta.env.PROD
  ? '/api'
  : `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api`;

export default function App() {
  const { plan, activeTab, setActiveTab, activeWeekIndex, fetchPlans } = usePlanStore();
  const { user, isLoading: authLoading } = useAuth();
  const fetchExercises = useCustomExercisesStore((s) => s.fetchExercises);
  const fetchSurveys = useSurveyStore((s) => s.fetchSurveys);

  // Landing page → login/register state
  const [showAuth, setShowAuth] = useState<null | 'login' | 'register'>(null);

  // ── Cold-start splash screen ──
  const [backendReady, setBackendReady] = useState(!import.meta.env.PROD);
  const [splashStatus, setSplashStatus] = useState('Łączenie z serwerem...');
  const retryCount = useRef(0);

  useEffect(() => {
    if (backendReady) return;

    let cancelled = false;

    const ping = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/health`, {
          method: 'GET',
          cache: 'no-store',
        });
        if (res.ok && !cancelled) {
          setBackendReady(true);
        }
      } catch {
        // Backend not ready yet
      }

      if (!cancelled && !backendReady) {
        retryCount.current++;
        if (retryCount.current >= 3) {
          setSplashStatus('Serwer się uruchamia... to może potrwać do 30 sekund');
        }
        if (retryCount.current >= 8) {
          setSplashStatus('Prawie gotowe... jeszcze chwila');
        }
        setTimeout(ping, 3000);
      }
    };

    ping();

    return () => { cancelled = true; };
  }, [backendReady]);

  // ── Public survey fill ──
  const surveyFillId = (() => {
    const url = new URL(window.location.href);

    // Path-based links: /survey/fill/:token
    const pathMatch = url.pathname.match(/^\/survey\/fill\/(.+)$/);
    if (pathMatch) return decodeURIComponent(pathMatch[1]);

    // Hash-based links (legacy/current share format):
    // #survey/:token or #/survey/fill/:token
    const hash = url.hash.replace(/^#/, '');
    const hashMatch = hash.match(/^\/?survey(?:\/fill)?\/(.+)$/);
    if (hashMatch) return decodeURIComponent(hashMatch[1]);

    return null;
  })();

  // ── Fetch user data after auth ──
  const userId = user?.id;
  useEffect(() => {
    if (userId) {
      fetchPlans();
      fetchExercises();
      fetchSurveys();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const coachSuggestions = useMemo(() => {
    if (!plan) return [];
    const week = plan.weeks[activeWeekIndex];
    if (!week) return [];
    const volume = calculateVolumeMetrics(week, plan.trainingLevel);
    const fatigue = calculateFatigueMetrics(week, plan.fatigueTolerance);
    return generateCoachSuggestions(week, volume, fatigue, plan.trainingLevel, plan.progressionModel);
  }, [plan, activeWeekIndex]);

  // ── Cold-start splash ──
  if (!backendReady) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center gap-6">
        <div className="flex items-center gap-3 select-none">
          <div className="w-14 h-14 rounded-2xl bg-brand-600 flex items-center justify-center shadow-lg shadow-brand-600/20">
            <Dumbbell size={28} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">
              APEX<span className="text-brand-500">Builder</span>
            </h1>
            <p className="text-xs text-slate-500 font-medium">Twój kreator planów treningowych</p>
          </div>
        </div>

        <div className="flex flex-col items-center gap-3">
          <Loader2 size={28} className="text-brand-500 animate-spin" />
          <p className="text-sm text-slate-400 animate-pulse">{splashStatus}</p>
        </div>

        <p className="text-[10px] text-slate-600 mt-8">
          Darmowy hosting - serwer budzi się po nieaktywności
        </p>
      </div>
    );
  }

  // ── Public survey fill page (no login required) ──
  if (surveyFillId) {
    return (
      <main className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 transition-colors">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 py-8">
          <SurveyFillPage surveyId={surveyFillId} />
        </div>
        <CookieConsent />
      </main>
    );
  }

  // ── Public Terms / Privacy pages (no login required) ──
  if (window.location.pathname === '/terms') {
    return (
      <main className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4 sm:p-8">
        <TermsPage publicMode />
      </main>
    );
  }

  if (window.location.pathname === '/privacy') {
    return (
      <main className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4 sm:p-8">
        <PrivacyPage publicMode />
      </main>
    );
  }

  // ── Auth loading state ──
  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={32} className="text-brand-600 animate-spin mx-auto mb-2" />
          <p className="text-sm text-slate-500">Ładowanie...</p>
        </div>
      </div>
    );
  }

  // ── Not logged in → Landing page or Login ──
  if (!user) {
    if (showAuth) {
      return (
        <>
          <LoginScreen
            initialMode={showAuth}
            onBack={() => setShowAuth(null)}
          />
          <CookieConsent />
        </>
      );
    }
    return (
      <>
        <LandingPage
          onLogin={() => setShowAuth('login')}
          onRegister={() => setShowAuth('register')}
        />
        <CookieConsent />
      </>
    );
  }

  return (
    <Layout>
      {/* ── Legend accessible always ──── */}
      {activeTab === 'legend' && <LegendPage />}

      {/* ── Dashboard ─────────────────── */}
      {activeTab === 'dashboard' && <PlanDashboard />}

      {/* ── Exercise database ─────────── */}
      {activeTab === 'exercises' && <ExerciseDatabase />}

      {/* ── Survey manager (trainers) ─── */}
      {activeTab === 'survey' && <SurveyBuilder />}

      {/* ── Calculators ────────────────── */}
      {activeTab === 'calculators' && <CalculatorsPage />}

      {/* ── Terms ──────────────────────── */}
      {activeTab === 'terms' && <TermsPage />}

      {/* ── Privacy ────────────────────── */}
      {activeTab === 'privacy' && <PrivacyPage />}

      {/* ── Plan Comparison ────────────── */}
      {activeTab === 'comparison' && <PlanComparison />}

      {/* ── No plan, not special tab → show dashboard ── */}
      {!plan && !['legend', 'dashboard', 'exercises', 'survey', 'calculators', 'terms', 'privacy', 'comparison'].includes(activeTab) && <PlanDashboard />}

      {/* ── Has plan ─────────────────────── */}
      {plan && !['legend', 'dashboard', 'exercises', 'survey', 'calculators', 'terms', 'privacy', 'comparison'].includes(activeTab) && (
        <>
          {activeTab === 'builder' && (
            <div>
              <PlanConfig />
              <WeekView />
            </div>
          )}

          {activeTab === 'analytics' && <AnalyticsDashboard />}

          {activeTab === 'coach' && <CoachPanel suggestions={coachSuggestions} />}
        </>
      )}
      <CookieConsent />
    </Layout>
  );
}
